/**
 * Custom hooks for fetching orders and related data from Firestore
 */

import { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  limit,
  where,
  onSnapshot,
  QueryConstraint,
  doc,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { wywolajFunkcje } from '../services/funkcje';
import { Order, LogEntry, QueueStatus, OrderStatus, BotStatus } from '../types';

/**
 * Real-time listener for orders
 */
export function useOrders(statusFilter?: OrderStatus, limitCount = 50) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const constraints: QueryConstraint[] = [];

    if (statusFilter) {
      constraints.push(where('status', '==', statusFilter));
    }

    constraints.push(orderBy('created_at', 'desc'));
    constraints.push(limit(limitCount));

    const q = query(collection(db, 'orders'), ...constraints);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const ordersData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Order[];

        setOrders(ordersData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching orders:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [statusFilter, limitCount]);

  return { orders, loading, error };
}

/**
 * Fetches a single order by ID
 */
export function useOrder(orderId: string | undefined) {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }

    const orderRef = doc(db, 'orders', orderId);

    const unsubscribe = onSnapshot(
      orderRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setOrder({ id: snapshot.id, ...snapshot.data() } as Order);
        } else {
          setOrder(null);
          setError('Order not found');
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching order:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [orderId]);

  return { order, loading, error };
}

/**
 * Real-time listener for logs of a specific order
 */
export function useOrderLogs(orderId: string | undefined) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'logs'),
      where('order_id', '==', orderId),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const logsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as LogEntry[];

        setLogs(logsData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching logs:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [orderId]);

  return { logs, loading, error };
}

/**
 * Real-time listener for queue status
 */
export function useQueueStatus() {
  const [queueStatus, setQueueStatus] = useState<QueueStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const queueRef = doc(db, 'queue', 'current');

    const unsubscribe = onSnapshot(
      queueRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setQueueStatus({ id: snapshot.id, ...snapshot.data() } as QueueStatus);
        } else {
          setQueueStatus(null);
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching queue status:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return { queueStatus, loading, error };
}

/**
 * Gets statistics about orders
 */
export function useOrderStats() {
  const { orders: allOrders } = useOrders(undefined, 1000);

  const stats = {
    total: allOrders.length,
    pending: allOrders.filter((o) => o.status === 'pending').length,
    processing: allOrders.filter((o) => o.status === 'processing').length,
    completed: allOrders.filter((o) => o.status === 'completed').length,
    failed: allOrders.filter((o) => o.status === 'failed').length,
    partial: allOrders.filter((o) => o.status === 'partial').length,
    orderedAwaitingPayment: allOrders.filter((o) => o.status === 'ordered_awaiting_payment').length,
    reservation: allOrders.filter((o) => o.status === 'reservation').length,
    orderedByWoocommerce: allOrders.filter((o) => o.status === 'ordered_by_woocommerce').length,
    productUnavailable: allOrders.filter((o) => o.status === 'product_unavailable').length,
  };

  return stats;
}

/**
 * Real-time listener for bot status (live preview)
 */
export function useBotStatus() {
  const [botStatus, setBotStatus] = useState<BotStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const statusRef = doc(db, 'bot_status', 'current');

    const unsubscribe = onSnapshot(
      statusRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setBotStatus({
            id: snapshot.id,
            ...snapshot.data(),
          } as BotStatus);
        } else {
          setBotStatus({
            id: 'current',
            state: 'idle',
            updated_at: new Date(),
          });
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching bot status:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return { botStatus, loading, error };
}

/**
 * Sync result for a single order
 */
export interface OrderSyncResult {
  orderId: string;
  woocommerceOrderId: string;
  synced: boolean;
  hasSupplierOrder: boolean;
  supplierOrderId: string | null;
  wcSyncStatus?: string; // 'synced' | 'not_found_in_wc' | 'wc_status_changed' | 'wc_trashed'
  skipped?: boolean;
  skipReason?: string;
  error?: string;
  updated?: boolean; // Whether any data was actually updated
}

/**
 * Cleanup result summary
 */
export interface CleanupResult {
  success: boolean;
  total: number;
  synced: number;
  summary: {
    not_found_in_wc: number;
    wc_status_changed: number;
    wc_trashed: number;
    still_processing: number;
  };
  results: OrderSyncResult[];
}

/**
 * Progress state for sync operations
 */
export interface SyncProgress {
  total: number;
  current: number;
  currentOrderId?: string;
  status: 'idle' | 'syncing' | 'done' | 'error';
  message?: string;
}

/**
 * Hook for syncing orders with WooCommerce
 */
export function useOrderSync() {
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<SyncProgress>({
    total: 0,
    current: 0,
    status: 'idle',
  });

  /**
   * Reset progress state
   */
  const resetProgress = () => {
    setProgress({ total: 0, current: 0, status: 'idle' });
  };

  /**
   * Sync a single order with WooCommerce
   */
  const syncOrder = async (orderId: string): Promise<OrderSyncResult | null> => {
    setSyncing(true);
    setError(null);
    setProgress({ total: 1, current: 0, status: 'syncing', currentOrderId: orderId });

    try {
      const response = await wywolajFunkcje(
        `syncOrderWithWooCommerce?orderId=${encodeURIComponent(orderId)}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      setProgress({ total: 1, current: 1, status: 'done' });
      return result as OrderSyncResult;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sync failed';
      setError(message);
      setProgress({ total: 1, current: 0, status: 'error', message });
      return null;
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Sync multiple orders with WooCommerce (batch) with progress tracking
   */
  const syncOrdersBatch = async (orderIds: string[], onProgress?: (progress: SyncProgress) => void): Promise<OrderSyncResult[]> => {
    if (orderIds.length === 0) return [];
    
    setSyncing(true);
    setError(null);
    
    const total = orderIds.length;
    setProgress({ total, current: 0, status: 'syncing', message: 'Rozpoczynam synchronizację...' });
    onProgress?.({ total, current: 0, status: 'syncing', message: 'Rozpoczynam synchronizację...' });

    try {
      // Split into smaller batches for progress updates (10 at a time)
      const BATCH_SIZE = 10;
      const allResults: OrderSyncResult[] = [];
      
      for (let i = 0; i < orderIds.length; i += BATCH_SIZE) {
        const batchIds = orderIds.slice(i, i + BATCH_SIZE);
        const batchStart = i;
        
        setProgress({
          total,
          current: batchStart,
          status: 'syncing',
          message: `Synchronizuję zamówienia ${batchStart + 1}-${Math.min(batchStart + BATCH_SIZE, total)} z ${total}...`,
        });
        onProgress?.({
          total,
          current: batchStart,
          status: 'syncing',
          message: `Synchronizuję zamówienia ${batchStart + 1}-${Math.min(batchStart + BATCH_SIZE, total)} z ${total}...`,
        });

        const response = await wywolajFunkcje('syncOrdersBatch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderIds: batchIds }),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        allResults.push(...(data.results as OrderSyncResult[]));
        
        setProgress({
          total,
          current: Math.min(batchStart + BATCH_SIZE, total),
          status: 'syncing',
          message: `Zsynchronizowano ${Math.min(batchStart + BATCH_SIZE, total)} z ${total} zamówień`,
        });
        onProgress?.({
          total,
          current: Math.min(batchStart + BATCH_SIZE, total),
          status: 'syncing',
          message: `Zsynchronizowano ${Math.min(batchStart + BATCH_SIZE, total)} z ${total} zamówień`,
        });
      }

      const updated = allResults.filter(r => r.synced).length;
      const withSupplier = allResults.filter(r => r.hasSupplierOrder).length;
      
      setProgress({
        total,
        current: total,
        status: 'done',
        message: `Gotowe! Zsynchronizowano ${updated} zamówień, ${withSupplier} już przetworzonych w WC.`,
      });
      onProgress?.({
        total,
        current: total,
        status: 'done',
        message: `Gotowe! Zsynchronizowano ${updated} zamówień, ${withSupplier} już przetworzonych w WC.`,
      });

      return allResults;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Batch sync failed';
      setError(message);
      setProgress({ total, current: 0, status: 'error', message: `Błąd: ${message}` });
      onProgress?.({ total, current: 0, status: 'error', message: `Błąd: ${message}` });
      return [];
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Verify if an order can be processed (no supplier_order_id)
   */
  const verifyOrderForProcessing = async (orderId: string): Promise<{
    canProcess: boolean;
    reason?: string;
    supplierOrderId?: string;
  } | null> => {
    try {
      const response = await wywolajFunkcje(
        `verifyOrderForProcessing?orderId=${encodeURIComponent(orderId)}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      console.error('Error verifying order:', err);
      return null;
    }
  };

  /**
   * Cleanup orders that may no longer be in wc-processing status
   * This forces a sync even for orders that have wc_sync_status set
   * Use this for one-time cleanup of stale orders
   */
  const cleanupStaleOrders = async (orderIds: string[], onProgress?: (progress: SyncProgress) => void): Promise<CleanupResult | null> => {
    if (orderIds.length === 0) return null;
    
    setSyncing(true);
    setError(null);
    
    const total = orderIds.length;
    setProgress({ total, current: 0, status: 'syncing', message: 'Rozpoczynam cleanup...' });
    onProgress?.({ total, current: 0, status: 'syncing', message: 'Rozpoczynam cleanup...' });

    try {
      const response = await wywolajFunkcje('cleanupStaleSyncOrders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderIds }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json() as CleanupResult;
      
      setProgress({
        total,
        current: total,
        status: 'done',
        message: `Cleanup zakończony! ${result.summary.still_processing} wciąż w processing, ${result.summary.wc_status_changed} ze zmienionym statusem, ${result.summary.not_found_in_wc} nie znaleziono w WC.`,
      });
      onProgress?.({
        total,
        current: total,
        status: 'done',
        message: `Cleanup zakończony!`,
      });

      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Cleanup failed';
      setError(message);
      setProgress({ total, current: 0, status: 'error', message: `Błąd: ${message}` });
      onProgress?.({ total, current: 0, status: 'error', message: `Błąd: ${message}` });
      return null;
    } finally {
      setSyncing(false);
    }
  };

  return {
    syncOrder,
    syncOrdersBatch,
    cleanupStaleOrders,
    verifyOrderForProcessing,
    syncing,
    error,
    progress,
    resetProgress,
  };
}
